/** Where the outcome of a write made from a drawer is shown - the Facilities place's one copy of what
 * the Organisation screen did inline.
 *
 * The forms the drawers hold (`EntityForm`, `FacilityForm`, the corrections, the lease answer, the
 * ownership and filing-window forms) hand their promise to `report(promise, said, again)`. The
 * result is shown in one place: a confirmation, **the server's own sentence on a refusal** (every
 * refusal on this surface is written to be read), and, for a write back-dated into a closed period,
 * an Admin's chance to send it again with a reason (ADR-0094). A write's result belongs to the
 * drawer it was made in: closing the drawer clears it.
 */

import { useState } from "react";

import { isPeriodClosed } from "../../api/refusal";
import { WriteFailed } from "../../components/Loaded";
import type { Report } from "../Organisation";
import { PostCloseRetry } from "../PeriodLock";
import styles from "./Facilities.module.css";

export function useReporter(onReload?: () => void) {
  const [message, setMessage] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [again, setAgain] = useState<{
    run: (postCloseReason: string) => Promise<unknown>;
    said: string;
  } | null>(null);
  const [retrying, setRetrying] = useState(false);

  const report: Report = (result, said, retry) => {
    setMessage(null);
    setRefusal(null);
    setAgain(null);
    return result.then(
      () => setMessage(said),
      (error: unknown) => {
        setRefusal(error);
        if (retry) setAgain({ run: retry, said });
      },
    );
  };

  const clear = () => {
    setMessage(null);
    setRefusal(null);
    setAgain(null);
  };

  const notes = (
    <div className={styles.notes}>
      {message ? (
        <p className={styles.saved} role="status" data-testid="org-message">
          {message}
        </p>
      ) : null}
      <PostCloseRetry
        error={refusal}
        pending={retrying}
        onRecord={
          again
            ? (reason) => {
                setRetrying(true);
                void report(again.run(reason), again.said, again.run).finally(() => setRetrying(false));
              }
            : undefined
        }
      />
      <WriteFailed
        error={isPeriodClosed(refusal) ? null : refusal}
        of="this workspace's structure"
        testId="org-error"
        onReload={() => {
          onReload?.();
          setRefusal(null);
        }}
      />
    </div>
  );
  return { report, notes, clear };
}
